import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth'

export interface User {
  id: string
  cognitoUserId: string
  email: string
  name: string
  jobTitle: string
  groups: string[]
  roles: string[]
  isAdmin: boolean
  agreementSigned: boolean
  // Excused from the agreement prompt (gen1 App.ts checkAgreement) — a simulation account
  // stands in for a real one and has no authority to accept terms on the facility's behalf.
  simulationUser: boolean
  facility: any
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Computed
  const isAuthenticated = computed(() => !!user.value)
  const userGroups = computed(() => user.value?.groups || [])
  const userName = computed(() => user.value?.name || user.value?.email || '')

  // Actions
  const fetchUser = async () => {
    loading.value = true
    error.value = null

    try {
      // fetchAuthSession() resolves without throwing when nobody is signed in, so use it
      // to detect the unauthenticated state up front. Otherwise getCurrentUser() throws
      // UserUnAuthenticatedException on every login-page visit — a normal state, not a
      // failure — which surfaced as a spurious "Failed to fetch user information" error.
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken
      if (!idToken) {
        user.value = null
        return
      }

      const currentUser = await getCurrentUser()

      // Extract groups from JWT token or SAML attributes
      const groups = idToken.payload['cognito:groups'] as string[] || []

      // Parse user object from custom claim
      let userData: any = {}
      let isAdmin = false
      let agreementSigned = false
      let simulationUser = false
      let facility: any = null
      let roles: string[] = []

      if (idToken?.payload.user) {
        try {
          userData = JSON.parse(idToken.payload.user as string)
          isAdmin = userData.isAdmin || false
          facility = userData.facility || null
          agreementSigned = facility?.agreementSigned || false
          simulationUser = userData.simulationUser || false
          roles = userData.roles || []
        } catch (e) {
          console.error('Error parsing user data from token:', e)
        }
      }

      user.value = {
        id: currentUser.userId,
        cognitoUserId: currentUser.userId,
        email: currentUser.signInDetails?.loginId || '',
        name: userData.name || idToken?.payload.name as string || '',
        jobTitle: userData.jobTitle || '',
        groups,
        roles,
        isAdmin,
        agreementSigned,
        simulationUser,
        facility
      }
    } catch (err) {
      console.error('Error fetching user:', err)
      error.value = 'Failed to fetch user information'
      user.value = null
    } finally {
      loading.value = false
    }
  }

  const refreshAuthToken = async () => {
    loading.value = true
    error.value = null

    try {
      const session = await fetchAuthSession({ forceRefresh: true })
      const idToken = session.tokens?.idToken

      // Parse user object from custom claim
      let userData: any = {}
      let isAdmin = false
      let agreementSigned = false
      let simulationUser = false
      let facility: any = null

      if (idToken?.payload.user) {
        try {
          userData = JSON.parse(idToken.payload.user as string)
          isAdmin = userData.isAdmin || false
          facility = userData.facility || null
          agreementSigned = facility?.agreementSigned || false
          simulationUser = userData.simulationUser || false
        } catch (e) {
          console.error('Error parsing user data from token:', e)
        }
      }

      if (user.value) {
        user.value.isAdmin = isAdmin
        user.value.agreementSigned = agreementSigned
        user.value.simulationUser = simulationUser
        user.value.facility = facility
        user.value.jobTitle = userData.jobTitle || ''
      }
    } catch (err) {
      console.error('Error refreshing auth token:', err)
      error.value = 'Failed to refresh authentication'
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      // A facility may nominate where its users land after signing out — typically back to the
      // customer's own portal (gen1 App.ts logOut). Read it BEFORE clearing user.value, which is
      // the only copy; the field rides in on the token's facility claim.
      const redirectUrl = user.value?.facility?.logoutRedirectUrl
      await signOut()
      user.value = null
      error.value = null
      window.location.href = redirectUrl || '/login'
    } catch (err) {
      console.error('Error signing out:', err)
      error.value = 'Failed to sign out'
    }
  }

  // Permission helpers
  const hasGroup = (groupName: string): boolean => {
    return userGroups.value.includes(groupName)
  }

  const hasAnyGroup = (groups: string[]): boolean => {
    return groups.some(group => userGroups.value.includes(group))
  }

  const hasAllGroups = (groups: string[]): boolean => {
    return groups.every(group => userGroups.value.includes(group))
  }

  return {
    // State
    user,
    loading,
    error,

    // Computed
    isAuthenticated,
    userGroups,
    userName,

    // Actions
    fetchUser,
    refreshAuthToken,
    logout,
    hasGroup,
    hasAnyGroup,
    hasAllGroups
  }
})
